/**
 * RegistryFieldEditor — a self-service panel on the uni-portal edit
 * pages that lets a claimed university admin verify any field in the
 * shared registry (`services/field_registry.py`) with the same
 * validation the admin surface uses. Each save writes an audit row
 * with `actor_type: "university"`, so a green tick appears next to
 * the value on the public course page.
 *
 * Layout: fields grouped by section, one row per field.
 *   [ Section: cost ]
 *     Annual fee growth · current: 3.0%     [3.4] [Verify]  ✓
 *     Application fee   · current: —        [   ] [Verify]
 *     …
 *
 * Only fields where `actor_scope in {"university","both"}` render.
 * Fields already verified in this session show a small green tick.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TYPE_PREFIX = { usd: "$", gbp: "£", percent: "%" };

function getDotted(doc, path) {
  if (!doc || !path) return undefined;
  return path.split(".").reduce((cur, seg) => (cur == null ? undefined : cur[seg]), doc);
}

function fmtCurrent(v, type) {
  if (v === null || v === undefined || v === "") return "—";
  if (type === "bool") return v ? "true" : "false";
  if (type === "percent" && typeof v === "number") return `${v}%`;
  if ((type === "usd" || type === "gbp") && typeof v === "number") {
    return `${TYPE_PREFIX[type]}${v.toLocaleString()}`;
  }
  return String(v).length > 60 ? String(v).slice(0, 60) + "…" : String(v);
}

function FeeEvidenceInputs({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const cls = "h-7 border border-stone-300 rounded px-1.5 text-[11px]";
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="fee-evidence-inputs">
      <input className={`${cls} w-56`} placeholder="Source URL (official fee page)" value={value.source_url} onChange={set("source_url")} data-testid="fee-evidence-url" />
      <input className={`${cls} w-20`} placeholder="2026-27" value={value.academic_year} onChange={set("academic_year")} data-testid="fee-evidence-year" />
      <select className={cls} value={value.includes_required_fees} onChange={set("includes_required_fees")} data-testid="fee-evidence-fees">
        <option value="">Required fees included?</option><option value="true">Includes required fees</option><option value="false">Tuition only</option>
      </select>
      <select className={cls} value={value.residency_basis} onChange={set("residency_basis")} data-testid="fee-evidence-residency">
        <option value="">Applies to…</option><option value="international">International students</option>
        <option value="out_of_state">US out-of-state students</option><option value="all_students">All students</option>
      </select>
    </div>
  );
}

function RegistryRow({ spec, currentValue, currentRevision, docType, docId, onSaved, apiPrefix = "uni-portal" }) {
  const [value, setValue] = useState("");
  const guarded = apiPrefix === "admin" && docType === "degrees";
  const attemptKey = useRef(null);  // reused on retry of the same edit, cleared on success or new value
  const [stale, setStale] = useState(false);
  useEffect(() => { attemptKey.current = null; }, [value]);  // a different payload needs a new key
  const [busy, setBusy] = useState(false);
  const [savedAfter, setSavedAfter] = useState(null);
  const needsFeeEvidence = spec.path === "cost.tuition_intl_annual_usd";
  const [feeEv, setFeeEv] = useState({ source_url: "", academic_year: "", includes_required_fees: "", residency_basis: "" });
  const feeEvReady = !needsFeeEvidence || (/^https?:\/\//.test(feeEv.source_url) && /^20\d{2}-\d{2}$/.test(feeEv.academic_year)
    && feeEv.includes_required_fees !== "" && feeEv.residency_basis !== "");
  // Long-text fields (max > 500) render on their own row with a
  // full-width 6-line textarea + Verify below. Pre-filling with the
  // current value on mount so staff can tweak instead of retype.
  const isLongText = spec.type === "text" && (spec.max || 0) > 500;
  useEffect(() => {
    if (!isLongText) return;
    if (savedAfter != null) return;
    if (currentValue == null || currentValue === "") return;
    // Only pre-fill once — after that the user's edits are canonical.
    if (value === "") setValue(String(currentValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLongText, currentValue]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy || value === "" || stale) return;
    setBusy(true);
    try {
      let v = value;
      if (spec.type === "bool") v = value === "true";
      const url = apiPrefix === "admin"
        ? `/admin/${docType}/${docId}/field`
        : `/uni-portal/${docType}/${docId}/field`;
      // Server caps the key at 80 chars and already binds it to doc/path/actor/payload, so a bare UUID suffices.
      if (guarded && !attemptKey.current) attemptKey.current = crypto.randomUUID();
      const { data } = await api.post(url, {
        section: spec.section,
        field_label: spec.label,
        new_value: v,
        ...(guarded ? {
          expected_before: currentValue ?? null,
          expected_revision: currentRevision ?? null,
          idempotency_key: attemptKey.current,
        } : {}),
        ...(needsFeeEvidence ? {
          source_url: feeEv.source_url,
          fee_evidence: {
            academic_year: feeEv.academic_year,
            includes_required_fees: feeEv.includes_required_fees === "true",
            residency_basis: feeEv.residency_basis,
          },
        } : {}),
      });
      setSavedAfter(data.after);
      attemptKey.current = null;
      if (guarded) setStale(true);  // shown value is now ahead of the loaded revision: reload before editing again
      if (!isLongText) setValue("");
      onSaved?.(data);
      toast.success(`Verified ${spec.label}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (guarded && err?.response?.status === 409 && d?.code && d.code !== "not_written_or_in_progress") {
        attemptKey.current = null;
        setStale(true);
        toast.error("This value changed since the page loaded. Nothing was saved - reload and check before editing.");
      } else {
        const status = err?.response?.status;
        const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).filter(Boolean).join("; ") : d?.detail;
        toast.error(`Save failed${status ? ` (${status})` : ""}${msg ? `: ${msg}` : ""}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const displayCurrent = savedAfter != null
    ? fmtCurrent(savedAfter, spec.type)
    : fmtCurrent(currentValue, spec.type);

  if (isLongText) {
    return (
      <form
        onSubmit={submit}
        className="py-3 border-b border-stone-100 last:border-0 space-y-2"
        data-testid={`registry-row-${spec.section}-${spec.label}`}
      >
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[13px] text-stone-900 font-medium leading-snug">
              {spec.label}
            </div>
            {spec.help && (
              <div className="text-[11px] text-stone-500 leading-snug">
                {spec.help}
              </div>
            )}
          </div>
          {savedAfter != null && (
            <BadgeCheck
              className="h-4 w-4 text-emerald-600 shrink-0"
              data-testid={`registry-saved-${spec.section}-${spec.label}`}
            />
          )}
        </div>
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={6}
          maxLength={spec.max || undefined}
          placeholder={currentValue ? "" : "Draft the section prose here…"}
          className="w-full text-[13px] leading-relaxed font-mono"
          data-testid={`registry-input-${spec.section}-${spec.label}`}
        />
        <div className="flex items-center justify-between gap-3">
          <div className="text-[10px] font-mono text-stone-400">
            {value.length}{spec.max ? ` / ${spec.max}` : ""}
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={busy || value === "" || value === String(currentValue ?? "")}
            data-testid={`registry-save-${spec.section}-${spec.label}`}
            className="h-8 text-[11px] bg-emerald-700 hover:bg-emerald-800 text-white"
          >
            {busy ? "Saving…" : "Verify"}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="flex items-start gap-3 py-2.5 border-b border-stone-100 last:border-0"
      data-testid={`registry-row-${spec.section}-${spec.label}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-stone-900 font-medium leading-snug truncate">
          {spec.label}
        </div>
        <div className="text-[11px] text-stone-500 leading-snug">
          Current: <span className="font-mono">{displayCurrent}</span>
          {spec.help && (
            <span className="hidden md:inline"> · {spec.help.slice(0, 90)}</span>
          )}
        </div>
        {needsFeeEvidence && <FeeEvidenceInputs value={feeEv} onChange={setFeeEv} />}
      </div>
      <div className="flex items-center gap-2">
        {spec.type === "bool" ? (
          <Select value={value} onValueChange={setValue}>
            <SelectTrigger className="h-8 w-24 text-xs" data-testid={`registry-input-${spec.section}-${spec.label}`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">true</SelectItem>
              <SelectItem value="false">false</SelectItem>
            </SelectContent>
          </Select>
        ) : spec.type === "text" ? (
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={1}
            className="h-8 text-xs min-h-8 w-48"
            data-testid={`registry-input-${spec.section}-${spec.label}`}
          />
        ) : (
          <div className="relative">
            {TYPE_PREFIX[spec.type] && (
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-xs">
                {TYPE_PREFIX[spec.type]}
              </span>
            )}
            <Input
              type="number"
              step={["percent", "float"].includes(spec.type) ? "0.01" : "1"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className={`h-8 text-xs w-28 ${TYPE_PREFIX[spec.type] ? "pl-6" : ""}`}
              data-testid={`registry-input-${spec.section}-${spec.label}`}
            />
          </div>
        )}
        <Button
          type="submit"
          size="sm"
          disabled={busy || value === "" || !feeEvReady}
          data-testid={`registry-save-${spec.section}-${spec.label}`}
          className="h-8 text-[11px] bg-emerald-700 hover:bg-emerald-800 text-white"
        >
          Verify
        </Button>
        {savedAfter != null && (
          <BadgeCheck
            className="h-4 w-4 text-emerald-600"
            data-testid={`registry-saved-${spec.section}-${spec.label}`}
          />
        )}
      </div>
    </form>
  );
}

export default function RegistryFieldEditor({ docType, docId, doc, apiPrefix = "uni-portal" }) {
  const [registry, setRegistry] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = apiPrefix === "admin"
          ? "/admin/field-edits/registry"
          : "/uni-portal/registry";
        const { data } = await api.get(url);
        if (!cancelled) setRegistry(data.fields || []);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [apiPrefix]);

  const grouped = useMemo(() => {
    if (!registry) return null;
    const target = docType === "degrees" ? "degrees" : "universities";
    const out = new Map();
    for (const f of registry) {
      if (f.collection !== target) continue;
      if (!out.has(f.section)) out.set(f.section, []);
      out.get(f.section).push(f);
    }
    return out;
  }, [registry, docType]);

  const onSaved = useCallback(() => {
    // Consumer can refetch the doc if desired; we intentionally do
    // NOT auto-refetch — keeps typing snappy and the local savedAfter
    // state gives immediate feedback.
  }, []);

  if (err) {
    return (
      <div className="mt-8 p-4 border border-rose-200 bg-rose-50 rounded text-[12px] text-rose-800">
        Could not load registry: {err}
      </div>
    );
  }
  if (!grouped) {
    return (
      <div className="mt-8 p-4 border border-stone-200 rounded text-[12px] text-stone-500">
        Loading registry…
      </div>
    );
  }
  if (grouped.size === 0) return null;

  return (
    <div
      className="mt-8 border border-emerald-200 rounded-xl bg-white overflow-hidden"
      data-testid="registry-field-editor"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 bg-emerald-50 border-b border-emerald-200 hover:bg-emerald-100/70 transition-colors"
        data-testid="registry-field-editor-toggle"
      >
        <div className="flex items-center gap-2 text-left">
          <BadgeCheck className="h-4 w-4 text-emerald-700" />
          <div>
            <div className="font-editorial text-lg text-stone-900 leading-tight">
              Verify data on this page
            </div>
            <div className="text-[11px] text-stone-600 leading-snug">
              Every field you verify shows a green tick to families with
              your university's name. Uses the same registry as WUA staff.
            </div>
          </div>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-emerald-700" />
        ) : (
          <ChevronDown className="h-4 w-4 text-emerald-700" />
        )}
      </button>

      {open && (
        <div className="px-5 py-3 divide-y divide-stone-100">
          {[...grouped.entries()].map(([section, fields]) => (
            <div key={section} className="py-3 first:pt-0 last:pb-0">
              <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-1">
                {section}
              </div>
              {fields.map((f) => (
                <RegistryRow
                  key={`${section}::${f.label}`}
                  spec={f}
                  currentValue={getDotted(doc, f.path)}
                  currentRevision={getDotted(doc, `${f.path}__last_edited_at`)}
                  docType={docType}
                  docId={docId}
                  onSaved={onSaved}
                  apiPrefix={apiPrefix}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
